/**
 * Budget vs. actual. Amounts are cents as the P&L shows them: income and
 * expenses are both positive. Variance = actual − budget; whether that is
 * good depends on the kind of account (more income is good, more cost is not).
 */
import type { Cents } from "../money";
import { Exact } from "./exact";
import { displayKpi } from "./kpis";

export type AccountKind = "income" | "other_income" | "cogs" | "expense" | "other_expense";

/** QuickBooks account type → budget kind. Balance-sheet accounts have no kind and can't be budgeted. */
export function kindOfAccountType(type: string): AccountKind | null {
  switch (type) {
    case "Income":
      return "income";
    case "Other Income":
      return "other_income";
    case "Cost of Goods Sold":
      return "cogs";
    case "Expense":
      return "expense";
    case "Other Expense":
      return "other_expense";
    default:
      return null;
  }
}

const INCOME_KINDS = new Set<AccountKind>(["income", "other_income"]);

/** Shown instead of a percentage of a zero or negative budget. */
export const NOT_MEANINGFUL = "n/m";
/** The plain-language explanation shown with "n/m" (as a tooltip or legend). */
export const NOT_MEANINGFUL_HELP = "n/m = not meaningful: the budget is zero or negative, so a percentage of it can't be read. The dollar variance is shown.";

/**
 * Variance as a percentage of the budget, or explicitly not meaningful. A screen gets either a
 * finished display string or "n/m" with its reason; there is no number to divide again.
 */
export type VariancePct = { kind: "percent"; exact: string; display: string } | { kind: "not_meaningful"; display: typeof NOT_MEANINGFUL; reason: string };

/** Variance % = (actual − budget) ÷ budget, only when the budget is above zero. */
export function variancePct(budget: Cents, dollars: Cents): VariancePct {
  if (budget <= 0) return { kind: "not_meaningful", display: NOT_MEANINGFUL, reason: budget === 0 ? "Nothing was budgeted, so a percentage can't be calculated" : "The budget is negative (a budgeted loss or net expense), so a percentage of it would mislead" };
  const v = Exact.int(dollars).div(Exact.int(budget));
  return { kind: "percent", exact: v.toString(), display: displayKpi("percent", v) };
}

/** Dollars are always there; the percentage may be "n/m". */
export type Variance = { budget: Cents; actual: Cents; dollars: Cents; pct: VariancePct; favorable: boolean | null };

export function variance(kind: AccountKind | "net_income", budget: Cents, actual: Cents): Variance {
  const dollars = actual - budget;
  const higherIsGood = kind === "net_income" || INCOME_KINDS.has(kind);
  const favorable = dollars === 0 ? null : higherIsGood ? dollars > 0 : dollars < 0;
  return { budget, actual, dollars, pct: variancePct(budget, dollars), favorable };
}

const sum = (v: Cents[]) => v.reduce((a, b) => a + b, 0);

export type BudgetLineInput = { accountId: string; label: string; kind: AccountKind; budget: Cents[]; actual: Cents[] };

export type BvaRow = { accountId: string | null; label: string; kind: AccountKind | "net_income"; month: Variance; ytd: Variance };

/**
 * Compares budget with actual for month `monthIndex` (0–11) and for the year to date through it.
 * Returns account rows, a total per kind, and net income (income − costs) for both.
 */
export function budgetVsActual(lines: BudgetLineInput[], monthIndex: number): { rows: BvaRow[]; totals: BvaRow[]; netIncome: BvaRow } {
  if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) throw new RangeError("monthIndex must be 0–11");
  for (const l of lines) if (l.budget.length !== 12 || l.actual.length !== 12) throw new RangeError("Twelve months of budget and actual are required");
  const ytd = (v: Cents[]) => sum(v.slice(0, monthIndex + 1));
  const rows = lines.map((l) => ({
    accountId: l.accountId,
    label: l.label,
    kind: l.kind,
    month: variance(l.kind, l.budget[monthIndex]!, l.actual[monthIndex]!),
    ytd: variance(l.kind, ytd(l.budget), ytd(l.actual)),
  }));
  const kinds: AccountKind[] = ["income", "cogs", "expense", "other_income", "other_expense"];
  const LABELS: Record<AccountKind, string> = { income: "Total income", cogs: "Total cost of goods sold", expense: "Total expenses", other_income: "Total other income", other_expense: "Total other expenses" };
  const totals = kinds
    .filter((k) => lines.some((l) => l.kind === k))
    .map((k) => {
      const of = lines.filter((l) => l.kind === k);
      const m = (pick: "budget" | "actual", ytdToo: boolean) => sum(of.map((l) => (ytdToo ? ytd(l[pick]) : l[pick][monthIndex]!)));
      return { accountId: null, label: LABELS[k], kind: k, month: variance(k, m("budget", false), m("actual", false)), ytd: variance(k, m("budget", true), m("actual", true)) };
    });
  const net = (pick: "budget" | "actual", ytdToo: boolean) =>
    sum(lines.map((l) => (INCOME_KINDS.has(l.kind) ? 1 : -1) * (ytdToo ? ytd(l[pick]) : l[pick][monthIndex]!)));
  const netIncome: BvaRow = { accountId: null, label: "Net income", kind: "net_income", month: variance("net_income", net("budget", false), net("actual", false)), ytd: variance("net_income", net("budget", true), net("actual", true)) };
  return { rows, totals, netIncome };
}

/** Last year's amount grown by a percentage ("5" = +5%), rounded to the cent by the one rounding rule. */
export function grow(cents: Cents, percent: string): Cents {
  return Exact.int(cents).mul(Exact.int(1).add(Exact.decimal(percent).div(Exact.int(100)))).toCents();
}
